import {Component, Host, h, State, Prop, Watch, Event, EventEmitter} from '@stencil/core';
import { MobilePickerItem } from "../../utils"

@Component({
  tag: 'snap-mobile-picker',
  styleUrl: 'snap-mobile-picker.scss',
})
export class SnapMobilePicker {
  @Prop({ reflect: true }) rightAligned = false
  @Prop() options: Array<MobilePickerItem> = null
  @Prop({ reflect: true}) value = null

  @State() localOptions = {
    el: "",
    type: "infinite",
    count: 20,
    sensitivity: 0.8,
    source: [],
    value: null,
  }

  @State() halfCount = null
  @State() quarterCount = null
  @State() a = null
  @State() minV = null
  @State() selected = null
  @State() exceedA = null
  @State() moveT = null
  @State() moving = null
  @State() elems = {
    el: null,
    circleList: null,
    circleItems: null,
    highlight: null,
    highlightList: null,
    highListItems: null
  }
  @State() events = {
    touchstart: null,
    touchmove: null,
    touchend: null
  }
  @State() itemHeight = null
  @State() itemAngle = null
  @State() radius = null
  @State() scroll = null

  private rootRef: HTMLElement

  @Event({ eventName: 'snap-mobile-picker-selected', bubbles: true, composed: true }) snapMobilePickerSelected: EventEmitter

  @Watch('options')
  onOptionsUpdate() {
    if (!this.options) return

    this.destroy()
    this.init()
  }

  @Watch('value')
  onValueChange() {
    if (!this.value) return

    this.localOptions.value = this.value
    this.select(this.value, true)
  }


  componentDidLoad() {
    this.onOptionsUpdate()
    this.onValueChange()
  }

  init() {
    this.localOptions.count = this.localOptions.count - (this.localOptions.count % 4)
    this.halfCount = this.localOptions.count / 2;
    this.quarterCount = this.localOptions.count / 4;
    this.a = this.localOptions.sensitivity * 10;
    this.minV = Math.sqrt(1 / this.a);
    this.selected = this.localOptions.source[0];

    this.exceedA = 10;
    this.moveT = 0;
    this.moving = false;

    this.events = {
      touchstart: null,
      touchmove: null,
      touchend: null
    };

    this.localOptions.source = [...this.options]

    this.elems = {
      el: this.rootRef,
      circleList: null,
      circleItems: null,
      highlight: null,
      highlightList: null,
      highListItems: null
    }

    this.itemHeight = 28;
    this.itemAngle = 360 / this.localOptions.count / 2;
    this.radius = this.itemHeight / Math.tan((this.itemAngle * Math.PI) / 180);
    this.scroll = 0;

    this.create(this.localOptions.source);

    const touchData = {
      startY: 0,
      yArr: []
    };

    for (const eventName in this.events) {
      this.events[eventName] = ((eventName) => {
        return (e) => {
          if (this.elems.el.contains(e.target) || e.target === this.elems.el) {
            e.preventDefault();
            if (this.localOptions.source.length) {
              this['_' + eventName](e, touchData);
            }
          }
        };
      })(eventName);
    }

    this.elems.el.addEventListener('touchstart', this.events.touchstart);
    document.addEventListener('mousedown', this.events.touchstart);
    this.elems.el.addEventListener('touchend', this.events.touchend);
    document.addEventListener('mouseup', this.events.touchend);
    if (this.localOptions.source.length) {
      this.localOptions.value = this.localOptions.value !== null ? this.localOptions.value : this.localOptions.source[0].value;
      this.select(this.localOptions.value);
    }
  }

  _touchstart(e, touchData) {
    this.elems.el.addEventListener('touchmove', this.events.touchmove);
    document.addEventListener('mousemove', this.events.touchmove);
    const eventY = e.clientY || e.touches[0].clientY;
    touchData.startY = eventY;
    touchData.yArr = [[eventY, new Date().getTime()]];
    touchData.touchScroll = this.scroll;
    this._stop();
  }

  _touchmove(e, touchData) {
    const eventY = e.clientY || e.touches[0].clientY;
    touchData.yArr.push([eventY, new Date().getTime()]);
    if (touchData.length > 5) {
      touchData.unshift();
    }

    const scrollAdd = (touchData.startY - eventY) / this.itemHeight;
    let moveToScroll = scrollAdd + this.scroll;

    if (this.localOptions.type === 'normal') {
      if (moveToScroll < 0) {
        moveToScroll *= 0.3;
      }
      else if (moveToScroll > this.localOptions.source.length) {
        moveToScroll = this.localOptions.source.length + (moveToScroll - this.localOptions.source.length) * 0.3;
      }
    }
    else {
      moveToScroll = this._normalizeScroll(moveToScroll);
    }

    touchData.touchScroll = this._moveTo(moveToScroll);
  }

  _touchend(_e, touchData) {
    this.elems.el.removeEventListener('touchmove', this.events.touchmove);
    document.removeEventListener('mousemove', this.events.touchmove);
    let v;
    if (touchData.yArr.length === 1) {
      v = 0;
    }
    else {
      const startTime = touchData.yArr[touchData.yArr.length - 2][1];
      const endTime = touchData.yArr[touchData.yArr.length - 1][1];
      const startY = touchData.yArr[touchData.yArr.length - 2][0];
      const endY = touchData.yArr[touchData.yArr.length - 1][0];

      v = ((startY - endY) / this.itemHeight) * 1000 / (endTime - startTime);
      const sign = v > 0 ? 1 : -1;
      v = Math.abs(v) > 30 ? 30 * sign : v;
    }

    this.scroll = touchData.touchScroll;
    this._animateMoveByInitV(v);
  }

  create(source) {
    if (!source.length) {
      return;
    }

    const template = `
      <div class="snap-mobile-picker-container">
        <ul class="select-options" style="transform: translate3d(0, 0, ${-this.radius}px) rotateX(0deg);">
          {{circleListHTML}}
        </ul>
        <div class="highlight">
          <ul class="highlight-list">
            {{highListHTML}}
          </ul>
        </div>
      </div>
    `;

    if (this.localOptions.type === 'infinite') {
      let concatSource = [].concat(source);
      while (concatSource.length < this.halfCount) {
        concatSource = concatSource.concat(source);
      }
      source = concatSource;
    }
    this.localOptions.source = source;
    const sourceLength = source.length;

    let circleListHTML = '';
    for (let i = 0; i < source.length; i++) {
      circleListHTML += `<li class="select-option"
                    style="
                      top: ${this.itemHeight * -0.5}px;
                      height: ${this.itemHeight}px;
                      line-height: ${this.itemHeight}px;
                      transform: rotateX(${-this.itemAngle * i}deg) translate3d(0, 0, ${this.radius}px);
                    "
                    data-index="${i}"
                    >${source[i].text}</li>`
    }

    let highListHTML = '';
    for (let i = 0; i < source.length; i++) {
      highListHTML += `<li class="highlight-item" style="height: ${this.itemHeight}px;">
                        ${source[i].text}
                      </li>`
    }

    if (this.localOptions.type === 'infinite') {
      for (let i = 0; i < this.quarterCount; i++) {
        circleListHTML = `<li class="select-option"
                      style="
                        top: ${this.itemHeight * -0.5}px;
                        height: ${this.itemHeight}px;
                        line-height: ${this.itemHeight}px;
                        transform: rotateX(${this.itemAngle * (i + 1)}deg) translate3d(0, 0, ${this.radius}px);
                      "
                      data-index="${-i - 1}"
                      >${source[sourceLength - i - 1].text}</li>` + circleListHTML;

        circleListHTML += `<li class="select-option"
                      style="
                        top: ${this.itemHeight * -0.5}px;
                        height: ${this.itemHeight}px;
                        line-height: ${this.itemHeight}px;
                        transform: rotateX(${-this.itemAngle * (i + sourceLength)}deg) translate3d(0, 0, ${this.radius}px);
                      "
                      data-index="${i + sourceLength}"
                      >${source[i].text}</li>`;
      }
      highListHTML = `<li class="highlight-item" style="height: ${this.itemHeight}px;">
                          ${source[sourceLength - 1].text}
                      </li>` + highListHTML;
      highListHTML += `<li class="highlight-item" style="height: ${this.itemHeight}px;">${source[0].text}</li>`
    }

    this.elems.el.innerHTML = template
      .replace('{{circleListHTML}}', circleListHTML)
      .replace('{{highListHTML}}', highListHTML);
    this.elems.circleList = this.elems.el.querySelector('.select-options');
    this.elems.circleItems = this.elems.el.querySelectorAll('.select-option');


    this.elems.highlight = this.elems.el.querySelector('.highlight');
    this.elems.highlightList = this.elems.el.querySelector('.highlight-list');
    this.elems.highListItems = this.elems.el.querySelectorAll('.highlight-item');

    if (this.localOptions.type === 'infinite') {
      this.elems.highlightList.style.top = -this.itemHeight + 'px';
    }

    this.elems.highlight.style.height = this.itemHeight + 'px';
    this.elems.highlight.style.lineHeight = this.itemHeight + 'px';
  }

  _normalizeScroll(scroll) {
    let normalizedScroll = scroll;

    while(normalizedScroll < 0) {
      normalizedScroll += this.localOptions.source.length;
    }
    normalizedScroll = normalizedScroll % this.localOptions.source.length;
    return normalizedScroll;
  }

  _moveTo(scroll) {
    if (this.localOptions.type === 'infinite') {
      scroll = this._normalizeScroll(scroll);
    }
    this.elems.circleList.style.transform = `translate3d(0, 0, ${-this.radius}px) rotateX(${this.itemAngle * scroll}deg)`;
    this.elems.highlightList.style.transform = `translate3d(0, ${-(scroll) * this.itemHeight}px, 0)`;

    [...this.elems.circleItems].forEach(itemElem => {
      if (Math.abs(itemElem.dataset.index - scroll) > this.quarterCount) {
        itemElem.style.visibility = 'hidden';
      } else {
        itemElem.style.visibility = 'visible';
      }
    });
    return scroll;
  }

  async _animateMoveByInitV(initV) {
    let initScroll;
    let finalScroll;

    let totalScrollLen;
    let a;
    let t;

    if (this.localOptions.type === 'normal') {
      if (this.scroll < 0 || this.scroll > this.localOptions.source.length - 1) {
        a = this.exceedA;
        initScroll = this.scroll;
        finalScroll = this.scroll < 0 ? 0 : this.localOptions.source.length - 1;
        totalScrollLen = initScroll - finalScroll;
        t = Math.sqrt(Math.abs(totalScrollLen / a));
        await this._animateToScroll(initScroll, finalScroll, t);
      }
      else {
        initScroll = this.scroll;
        a = initV > 0 ? -this.a : this.a;
        t = Math.abs(initV / a);
        totalScrollLen = initV * t + a * t * t / 2;
        finalScroll = Math.round(this.scroll + totalScrollLen);
        finalScroll = finalScroll < 0 ? 0 : (finalScroll > this.localOptions.source.length - 1 ? this.localOptions.source.length - 1 : finalScroll);

        totalScrollLen = finalScroll - initScroll;
        t = Math.sqrt(Math.abs(totalScrollLen / a));
        await this._animateToScroll(this.scroll, finalScroll, t, 'easeOutQuart');
      }
    }
    else {
      a = initV > 0 ? -this.a : this.a;
      t = Math.abs(initV / a);
      totalScrollLen = initV * t + a * t * t / 2;
      finalScroll = Math.round(this.scroll + totalScrollLen);
      await this._animateToScroll(this.scroll, finalScroll, t, 'easeOutQuart');
    }

    this._selectByScroll(this.scroll);
  }

  _animateToScroll(initScroll, finalScroll, t, easingName = 'easeOutQuart') {
    if (initScroll === finalScroll || t === 0) {
      this._moveTo(initScroll);
      return;
    }

    const start = new Date().getTime() / 1000;
    let pass = 0;
    const totalScrollLen = finalScroll - initScroll;

    return new Promise((resolve) => {
      this.moving = true;
      const tick = () => {
        pass = new Date().getTime() / 1000 - start;

        if (pass < t) {
          this.scroll = this._moveTo(initScroll + this.easing[easingName](pass / t) * totalScrollLen);
          this.moveT = requestAnimationFrame(tick);
        } else {
          resolve('');
          this._stop();
          this.scroll = this._moveTo(initScroll + totalScrollLen);
        }
      };
      tick();
    });
  }

  select(value, noAnimation = false) {
    for (let i = 0; i < this.localOptions.source.length; i++) {
      if (this.localOptions.source[i].value === value) {
        window.cancelAnimationFrame(this.moveT);
        if (!noAnimation) {
          const initScroll = this._normalizeScroll(this.scroll);
          const finalScroll = i;
          const t = Math.sqrt(Math.abs((finalScroll - initScroll) / this.a));
          this._animateToScroll(initScroll, finalScroll, t);
        }
        setTimeout(() => this._selectByScroll(i));
        return;
      }
    }
    throw new Error(
      `Cannot select value: ${value} is not available in current source ${JSON.stringify(this.localOptions.source)}`
    );
  }

  easing = {
    easeOutCubic: function (pos) {
      return Math.pow(pos - 1, 3) + 1;
    },
    easeOutQuart: function (pos) {
      return -(Math.pow(pos - 1, 4) - 1);
    }
  };

  _stop() {
    this.moving = false;
    cancelAnimationFrame(this.moveT);
  }

  _selectByScroll(scroll) {
    scroll = this._normalizeScroll(scroll) | 0;
    if (scroll > this.localOptions.source.length - 1) {
      scroll = this.localOptions.source.length - 1;
      this._moveTo(scroll);
    }
    this._moveTo(scroll);
    this.scroll = scroll;
    this.selected = this.localOptions.source[scroll];
    this.localOptions.value = this.selected.value;
    this.snapMobilePickerSelected.emit(this.selected)
  }

  destroy() {
    if (!this.elems.el) return

    this._stop()

    for (const eventName in this.events) {
      this.elems.el.removeEventListener(eventName, this.events[eventName]);
    }

    this.localOptions.value = null
    this.rootRef.innerHTML = "";
  }

  render() {
    return (
      <Host>
        <div class={`snap-mobile-picker ${this.rightAligned ? 'right-aligned' : 'left-aligned'}`}>
          <div ref={el => (this.rootRef = el)}></div>
        </div>
      </Host>
    )
  }
}
